import { useEffect, useRef, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
// MapLibre computes its worker URL at runtime, so the bundler never emits the file.
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import type {
  Map as MaplibreMap,
  Marker as MaplibreMarker,
  StyleSpecification,
} from 'maplibre-gl';

export type MapStyleName = 'Standard' | 'Hybrid';

// The MapLibre style spec requires absolute sprite/glyph URLs; the proxy rewrites
// them origin-relative, so the browser resolves them here where the origin is known.
function toAbsolute(url: string): string {
  return url.startsWith('/') ? window.location.origin + url : url;
}

async function loadStyle(styleName: MapStyleName): Promise<StyleSpecification> {
  const response = await fetch(`/api/maps/style/${styleName}`);
  if (!response.ok) {
    throw new Error(`Map style request failed: ${response.status}`);
  }
  const style = (await response.json()) as StyleSpecification;
  if (typeof style.sprite === 'string') {
    style.sprite = toAbsolute(style.sprite);
  } else if (Array.isArray(style.sprite)) {
    style.sprite = style.sprite.map((entry) => ({
      ...entry,
      url: toAbsolute(entry.url),
    }));
  }
  if (style.glyphs) {
    style.glyphs = toAbsolute(style.glyphs);
  }
  Object.values(style.sources ?? {}).forEach((source) => {
    if ('tiles' in source && Array.isArray(source.tiles)) {
      source.tiles = source.tiles.map(toAbsolute);
    }
  });
  return style;
}

type OnSelectRef = RefObject<
  ((latitude: number, longitude: number) => void) | undefined
>;

function createMap(
  maplibre: typeof import('maplibre-gl'),
  container: HTMLDivElement,
  style: StyleSpecification,
  center: [number, number],
  zoom: number,
  onSelectRef: OnSelectRef
): MaplibreMap {
  const map = new maplibre.Map({
    container,
    style,
    center,
    zoom,
    dragRotate: false,
    pitchWithRotate: false,
    attributionControl: false,
  });
  map.touchZoomRotate.disableRotation();
  map.addControl(new maplibre.AttributionControl({ compact: true }));
  // MapLibre auto-opens the compact attribution once its text loads; keep it
  // collapsed behind the toggle until the user opens it themselves.
  let attributionOpenedByUser = false;
  container.addEventListener(
    'click',
    (event) => {
      if (
        (event.target as HTMLElement).closest('.maplibregl-ctrl-attrib-button')
      ) {
        attributionOpenedByUser = true;
      }
    },
    true
  );
  const collapseAttribution = () => {
    if (attributionOpenedByUser) return;
    container
      .querySelectorAll('.maplibregl-compact-show')
      .forEach((element) =>
        element.classList.remove('maplibregl-compact-show')
      );
  };
  map.on('data', collapseAttribution);
  if (onSelectRef.current !== undefined) {
    map.on('click', (event) => {
      const wrapped = event.lngLat.wrap();
      onSelectRef.current?.(wrapped.lat, wrapped.lng);
    });
  }
  return map;
}

function createMarker(
  maplibre: typeof import('maplibre-gl'),
  map: MaplibreMap,
  lngLat: [number, number],
  onSelectRef: OnSelectRef
): MaplibreMarker {
  const interactive = onSelectRef.current !== undefined;
  const marker = new maplibre.Marker({ draggable: interactive })
    .setLngLat(lngLat)
    .addTo(map);
  if (interactive) {
    marker.on('dragend', () => {
      const position = marker.getLngLat().wrap();
      onSelectRef.current?.(position.lat, position.lng);
    });
  }
  return marker;
}
const DEFAULT_CENTER: [number, number] = [-98.5795, 39.8283];
const DEFAULT_ZOOM = 3;
const CENTER_FALLBACK_ZOOM = 12;
const POINT_ZOOM = 14;

type Props = {
  latitude?: number | null;
  longitude?: number | null;
  centerLatitude?: number | null;
  centerLongitude?: number | null;
  onSelect?: (latitude: number, longitude: number) => void;
  heightClassName?: string;
  mapStyle?: MapStyleName;
};

export function DeliveryAddressMap({
  latitude,
  longitude,
  centerLatitude,
  centerLongitude,
  onSelect,
  heightClassName = 'h-48',
  mapStyle = 'Standard',
}: Readonly<Props>) {
  const { t } = useTranslation('general');
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MaplibreMap | null>(null);
  const markerRef = useRef<MaplibreMarker | null>(null);
  const maplibreRef = useRef<typeof import('maplibre-gl') | null>(null);
  const coordinatesRef = useRef({ latitude, longitude });
  const centerFallbackRef = useRef({ centerLatitude, centerLongitude });
  const onSelectRef = useRef(onSelect);
  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);
  useEffect(() => {
    centerFallbackRef.current = { centerLatitude, centerLongitude };
  }, [centerLatitude, centerLongitude]);

  const { data: style } = useQuery({
    queryKey: ['map-style', mapStyle],
    queryFn: () => loadStyle(mapStyle),
    staleTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    const container = containerRef.current;
    if (style === undefined || container === null) return;

    if (mapRef.current !== null) {
      mapRef.current.setStyle(style);
      return;
    }

    let cancelled = false;

    void (async () => {
      const [maplibre] = await Promise.all([
        import('maplibre-gl'),
        import('maplibre-gl/dist/maplibre-gl.css'),
      ]);
      if (cancelled || mapRef.current !== null) return;
      maplibre.setWorkerUrl(maplibreWorkerUrl);

      maplibreRef.current = maplibre;
      const { latitude: lat, longitude: lng } = coordinatesRef.current;
      const { centerLatitude: fallbackLat, centerLongitude: fallbackLng } =
        centerFallbackRef.current;
      const hasPoint = lat != null && lng != null;
      const hasFallback = fallbackLat != null && fallbackLng != null;
      const [center, zoom]: [[number, number], number] = hasPoint
        ? [[lng, lat], POINT_ZOOM]
        : hasFallback
          ? [[fallbackLng, fallbackLat], CENTER_FALLBACK_ZOOM]
          : [DEFAULT_CENTER, DEFAULT_ZOOM];
      const map = createMap(
        maplibre,
        container,
        style,
        center,
        zoom,
        onSelectRef
      );
      if (hasPoint) {
        markerRef.current = createMarker(
          maplibre,
          map,
          [lng, lat],
          onSelectRef
        );
      }
      mapRef.current = map;
    })();

    return () => {
      cancelled = true;
    };
  }, [style]);

  useEffect(
    () => () => {
      mapRef.current?.remove();
      mapRef.current = null;
      markerRef.current = null;
      maplibreRef.current = null;
    },
    []
  );

  useEffect(() => {
    coordinatesRef.current = { latitude, longitude };
    const map = mapRef.current;
    const maplibre = maplibreRef.current;
    if (
      map === null ||
      maplibre === null ||
      latitude == null ||
      longitude == null
    ) {
      return;
    }
    map.setCenter([longitude, latitude]);
    if (markerRef.current === null) {
      markerRef.current = createMarker(
        maplibre,
        map,
        [longitude, latitude],
        onSelectRef
      );
    } else {
      markerRef.current.setLngLat([longitude, latitude]);
    }
  }, [latitude, longitude]);

  return (
    <div
      ref={containerRef}
      data-testid="delivery-address-map"
      aria-label={t('delivery_address_map.label')}
      className={`${heightClassName} w-full overflow-hidden rounded-lg border border-neut-200`}
    />
  );
}
